import { z } from 'zod';
import { ResourceVersionSchema } from './concurrency.js';
import { paginationQueryShape } from './pagination.js';
/**
 * Contratos de tipos de permiso, calendario, festivos y solicitudes (docs/migration/09 §6.6 a §6.8).
 * Fechas civiles `YYYY-MM-DD` y horas locales `HH:mm` sin zona ni desplazamiento (D-09); instantes en UTC.
 */
const Timestamp = z.iso.datetime();
const BooleanParam = z.enum(['true', 'false']).transform((value) => value === 'true');
const SearchParam = z.string().trim().min(2).max(100);
/** Fecha civil `YYYY-MM-DD` válida (sin zona). */
export const CivilDateSchema = z.iso.date();
/** Hora local `HH:mm` (sin segundos ni zona). */
export const LocalTimeSchema = z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/);
// ---------------------------------------------------------------- Tipos de permiso (09 §6.6)
const PermissionCode = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .pipe(z.string().regex(/^[A-Z0-9][A-Z0-9_-]{0,49}$/));
export const PermissionTypeSchema = z.strictObject({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    allowsDocument: z.boolean(),
    requiresDocument: z.boolean(),
    active: z.boolean(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
    version: ResourceVersionSchema,
});
/** "Requiere documento" exige "permite documento" (regla actual). El código se guarda en mayúsculas. */
export const PermissionTypeCreateSchema = z
    .strictObject({
    code: PermissionCode,
    name: z.string().trim().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    allowsDocument: z.boolean().optional(),
    requiresDocument: z.boolean().optional(),
})
    .refine((value) => !value.requiresDocument || value.allowsDocument === true, {
    message: 'requires_document_needs_allows_document',
    path: ['requiresDocument'],
});
/** Editar o desactivar (`PATCH` con `If-Match`). La coherencia de documentos se comprueba con el estado final. */
export const PermissionTypeUpdateSchema = z
    .strictObject({
    code: PermissionCode.optional(),
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    allowsDocument: z.boolean().optional(),
    requiresDocument: z.boolean().optional(),
    active: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'empty_update' });
export const PermissionTypeListQuerySchema = z.strictObject({
    ...paginationQueryShape,
    active: BooleanParam.optional(),
    q: SearchParam.optional(),
});
// ---------------------------------------------------------------- Calendario (09 §6.7)
export const CalendarSettingsSchema = z.strictObject({
    id: z.uuid(),
    effectiveFrom: CivilDateSchema,
    timeZone: z.string(),
    workdayStart: LocalTimeSchema,
    workdayEnd: LocalTimeSchema,
    slotMinutes: z.int().min(1),
    createdAt: Timestamp,
    createdBy: z.uuid().nullable(),
});
/**
 * Nueva configuración vigente desde hoy o desde una fecha posterior (D-09). La zona se valida como nombre IANA en el
 * servidor; por defecto `America/Bogota`.
 */
export const CalendarSettingsCreateSchema = z
    .strictObject({
    effectiveFrom: CivilDateSchema,
    timeZone: z.string().min(1).max(64).default('America/Bogota'),
    workdayStart: LocalTimeSchema,
    workdayEnd: LocalTimeSchema,
    slotMinutes: z.int().min(5).max(240),
})
    .refine((value) => value.workdayStart < value.workdayEnd, { message: 'workday_start_after_end', path: ['workdayEnd'] });
// ---------------------------------------------------------------- Festivos (09 §6.7)
export const HolidaySchema = z.strictObject({
    date: CivilDateSchema,
    name: z.string(),
    source: z.enum(['CALCULATED', 'MANUAL']),
    active: z.boolean(),
    disabledAt: Timestamp.nullable(),
});
export const HolidayListQuerySchema = z.strictObject({
    year: z
        .string()
        .regex(/^[0-9]{4}$/)
        .transform(Number)
        .pipe(z.int().min(2000).max(2100)),
});
export const HolidayCreateSchema = z.strictObject({
    date: CivilDateSchema,
    name: z.string().trim().min(1).max(200),
});
export const HolidayDateParamsSchema = z.strictObject({ date: CivilDateSchema });
// ---------------------------------------------------------------- Solicitudes (09 §6.8)
export const RequestStatusSchema = z.enum(['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'CANCELLED']);
/**
 * Nombre para mostrar de una persona (H-01, plan 11 §6). Solo identificador y nombre: nunca correo, UPN,
 * Object ID ni teléfono. Lo resuelve la API en la misma consulta, para que la web no necesite leer
 * empleados que su rol no le deja ver.
 */
export const DisplayPersonSchema = z.strictObject({ id: z.uuid(), fullName: z.string() });
/** Nombre para mostrar del tipo de permiso de una solicitud (H-01), aunque el tipo ya esté inactivo. */
export const DisplayPermissionTypeSchema = z.strictObject({ id: z.uuid(), name: z.string() });
// ---------------------------------------------------------------- Documentos (09 §6.11, D-07 y D-10)
/** Tipos admitidos (D-10): PDF, JPEG y PNG. La API también comprueba la firma del contenido. */
export const DOCUMENT_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
export const DocumentContentTypeSchema = z.enum(DOCUMENT_CONTENT_TYPES);
/** Tamaño máximo de un documento (D-10): 10 MB (10 × 1024 × 1024 bytes). */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
/**
 * Nombre del archivo tal como lo eligió quien lo adjunta (cabecera `X-File-Name`, codificada con
 * `encodeURIComponent`). Sin rutas (`/`, `\\`), caracteres de control ni nombres reservados (`.`, `..`).
 */
export const DocumentFileNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[^/\\\u0000-\u001f\u007f]+$/)
    .refine((value) => value !== '.' && value !== '..', { message: 'reserved_name' });
/**
 * Documento vigente de una solicitud (D-10: uno como máximo). Nunca lleva la ubicación en SharePoint: se
 * descarga con `GET /requests/{id}/documents/{documentId}/content`.
 */
export const RequestDocumentSchema = z.strictObject({
    id: z.uuid(),
    fileName: z.string(),
    contentType: DocumentContentTypeSchema,
    sizeBytes: z.int().min(1).max(MAX_DOCUMENT_BYTES),
    uploadedAt: Timestamp,
});
export const RequestDocumentParamsSchema = z.strictObject({ id: z.uuid(), documentId: z.uuid() });
export const RequestSchema = z.strictObject({
    id: z.uuid(),
    requestNumber: z.string().regex(/^PER-[0-9]{4}-[0-9]{6}$/),
    employeeId: z.uuid(),
    permissionTypeId: z.uuid(),
    approverId: z.uuid().nullable(),
    /** H-01: solicitante, aprobador almacenado (o `null`) y tipo, con su nombre. Los identificadores se conservan. */
    employee: DisplayPersonSchema,
    approver: DisplayPersonSchema.nullable(),
    permissionType: DisplayPermissionTypeSchema,
    permissionDate: CivilDateSchema,
    departureTime: LocalTimeSchema,
    returnTime: LocalTimeSchema,
    requestedHours: z.number(),
    actualHours: z.number().nullable(),
    reason: z.string(),
    status: RequestStatusSchema,
    submittedAt: Timestamp.nullable(),
    decidedAt: Timestamp.nullable(),
    decidedBy: z.uuid().nullable(),
    /** Canal de la decisión, derivado por el servidor: `EMAIL` solo con un código de aprobación válido; si no, `APP`. */
    decisionChannel: z.enum(['APP', 'EMAIL']).nullable(),
    /** Motivo del rechazo: se guarda en la solicitud y solo se muestra en el detalle. */
    rejectionReason: z.string().nullable(),
    cancelledAt: Timestamp.nullable(),
    cancelledBy: z.uuid().nullable(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
    version: ResourceVersionSchema,
    /** Documento vigente (09 §6.11) o `null`. Solo en el detalle y en las respuestas de las operaciones. */
    document: RequestDocumentSchema.nullable(),
});
/** Resumen para listados: sin el motivo, el motivo de rechazo (texto libre) ni el documento. */
export const RequestSummarySchema = RequestSchema.omit({ reason: true, rejectionReason: true, document: true });
const RequestFields = {
    permissionTypeId: z.uuid(),
    permissionDate: CivilDateSchema,
    departureTime: LocalTimeSchema,
    returnTime: LocalTimeSchema,
    reason: z.string().trim().min(1).max(2000),
};
/** Crear un borrador **para sí mismo**: no hay campo de empleado (09 §6.8). */
export const RequestCreateSchema = z
    .strictObject(RequestFields)
    .refine((value) => value.departureTime < value.returnTime, { message: 'departure_after_return', path: ['returnTime'] });
/** Editar el borrador (`PATCH` con `If-Match`); al menos un campo. */
export const RequestUpdateSchema = z
    .strictObject({
    permissionTypeId: RequestFields.permissionTypeId.optional(),
    permissionDate: RequestFields.permissionDate.optional(),
    departureTime: RequestFields.departureTime.optional(),
    returnTime: RequestFields.returnTime.optional(),
    reason: RequestFields.reason.optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'empty_update' });
/** Cancelar: comentario opcional (queda en el historial; nunca en el outbox). */
export const RequestCancelSchema = z.strictObject({ comment: z.string().trim().min(1).max(2000).optional() });
/** Ámbitos del listado (09 §6.8). `team` exige el rol RESPONSABLE; `all`, ADMINISTRADOR. */
export const RequestScopeSchema = z.enum(['mine', 'team', 'approvals', 'all']);
/** Lista blanca de `sort` (09 §4). El prefijo `-` invierte el campo. */
const REQUEST_SORT_FIELDS = ['permissionDate', 'createdAt', 'requestNumber'];
export const RequestSortSchema = z
    .string()
    .regex(/^(-?(?:permissionDate|createdAt|requestNumber))(?:,-?(?:permissionDate|createdAt|requestNumber)){0,2}$/)
    .superRefine((value, context) => {
    const fields = value.split(',').map((token) => (token.startsWith('-') ? token.slice(1) : token));
    if (new Set(fields).size !== fields.length)
        context.addIssue({ code: 'custom', message: 'duplicate_sort' });
})
    .transform((value) => value.split(',').map((token) => ({
    field: (token.startsWith('-') ? token.slice(1) : token),
    direction: token.startsWith('-') ? 'desc' : 'asc',
})));
/**
 * Filtros del listado (H-02, 09 §4). `from` y `to` son fechas civiles de la fecha del permiso, inclusivas.
 * `q` busca número, solicitante, tipo y aprobador almacenado. El ámbito se aplica antes que estos filtros.
 */
export const RequestListQuerySchema = z
    .strictObject({
    ...paginationQueryShape,
    scope: RequestScopeSchema.default('mine'),
    status: RequestStatusSchema.optional(),
    from: CivilDateSchema.optional(),
    to: CivilDateSchema.optional(),
    areaId: z.uuid().optional(),
    permissionTypeId: z.uuid().optional(),
    q: z.string().trim().min(1).max(100).optional(),
    sort: RequestSortSchema.optional(),
})
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, {
    message: 'from_after_to',
    path: ['to'],
});
export const RequestActionSchema = z.enum([
    'CREATED',
    'MODIFIED',
    'SUBMITTED',
    'APPROVED',
    'REJECTED',
    'CANCELLED',
    'ACTUAL_DURATION_RECORDED',
    'APPROVER_REASSIGNED',
    'EMPLOYEE_NOTIFIED',
    'MANAGER_NOTIFIED',
    'ADMIN_AREA_NOTIFIED',
]);
export const HistoryEventSchema = z.strictObject({
    id: z.uuid(),
    action: RequestActionSchema,
    previousStatus: RequestStatusSchema.nullable(),
    newStatus: RequestStatusSchema.nullable(),
    actorEmployeeId: z.uuid().nullable(),
    /** H-01: autor del evento con su nombre, o `null` si no hay autor (p. ej., un evento del sistema). */
    actor: DisplayPersonSchema.nullable(),
    channel: z.enum(['APP', 'EMAIL', 'SYSTEM', 'MIGRATION']),
    description: z.string(),
    comment: z.string().nullable(),
    occurredAt: Timestamp,
});
// ---------------------------------------------------------------- Fase 8: decisión, reasignación y duración real
/**
 * Código opaco del enlace de aprobación del correo (D-13): 32 bytes aleatorios en base64url (43
 * caracteres). No lleva la acción. El servidor solo guarda su SHA-256.
 */
export const ApprovalCodeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
/**
 * Decisión del aprobador almacenado (09 §6.8, §8). **Sin campo de canal**: lo deriva el servidor.
 * `reason` es obligatoria al rechazar y no se admite al aprobar.
 * `approvalCode` es el código del enlace del correo (D-13), si la pantalla se abrió desde él. Solo hace
 * que el servidor registre el canal `EMAIL` cuando el código es válido para esa solicitud y ese aprobador;
 * un código ausente, caducado o ajeno no impide decidir: el canal es `APP`.
 */
export const DecisionSchema = z
    .strictObject({
    decision: z.enum(['APPROVE', 'REJECT']),
    reason: z.string().trim().min(1).max(2000).optional(),
    approvalCode: ApprovalCodeSchema.optional(),
})
    .superRefine(reasonOnlyOnReject);
/**
 * Decisión desde la tarjeta de Outlook del aviso al responsable (Actionable Message, D-13). La envía Outlook
 * sin sesión; la autorizan el token firmado de Outlook y el código del correo, que liga la solicitud con el
 * aprobador. **Sin canal ni versión**: el canal es `EMAIL` y la versión la lee el servidor. `reason`, como
 * en `DecisionSchema`: obligatoria al rechazar (1 a 2000 caracteres) y no admitida al aprobar.
 */
export const EmailDecisionSchema = z
    .strictObject({
    decision: z.enum(['APPROVE', 'REJECT']),
    reason: z.string().trim().min(1).max(2000).optional(),
    approvalCode: ApprovalCodeSchema,
})
    .superRefine(reasonOnlyOnReject);
/** Respuesta a la tarjeta de Outlook: solo el estado resultante. */
export const EmailDecisionResultSchema = z.strictObject({ status: z.enum(['APPROVED', 'REJECTED']) });
function reasonOnlyOnReject(value, context) {
    if (value.decision === 'REJECT' && value.reason === undefined) {
        context.addIssue({ code: 'custom', path: ['reason'], message: 'reason_required_on_reject' });
    }
    if (value.decision === 'APPROVE' && value.reason !== undefined) {
        context.addIssue({ code: 'custom', path: ['reason'], message: 'reason_only_on_reject' });
    }
}
/** Reasignación del aprobador (solo administradores, solo en `PENDING_APPROVAL`). */
export const ReassignApproverSchema = z.strictObject({ approverId: z.uuid() });
/** Duración real (solo administradores, solo en `APPROVED`): > 0, ≤ 24 y como mucho 2 decimales. */
export const ActualDurationSchema = z.strictObject({
    actualHours: z
        .number()
        .gt(0)
        .lte(24)
        .refine((value) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-9, { message: 'max_two_decimals' }),
});
// ---------------------------------------------------------------- Auditoría (09 §6.9)
/**
 * Evento de auditoría. `changes` y `metadata` se devuelven **saneados**: sin claves de tokens,
 * secretos, cookies, motivos, comentarios ni datos de contacto.
 */
export const AuditEventSchema = z.strictObject({
    id: z.uuid(),
    occurredAt: Timestamp,
    actorEmployeeId: z.uuid().nullable(),
    actorType: z.enum(['USER', 'SYSTEM', 'JOB', 'MIGRATION']),
    action: z.string(),
    entityType: z.string(),
    entityId: z.uuid().nullable(),
    correlationId: z.uuid().nullable(),
    changes: z.record(z.string(), z.unknown()).nullable(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
});
/**
 * Instante de los filtros `from` y `to` de la auditoría: los escribe el administrador, así que admite la `Z`
 * o un desplazamiento numérico (`±HH:mm`). Un instante sin zona se rechaza. El resto de instantes del
 * contrato los emite la API en UTC y siguen siendo solo `Z`.
 */
const AuditFilterInstant = z.iso.datetime({ offset: true });
export const AuditEventListQuerySchema = z
    .strictObject({
    ...paginationQueryShape,
    entityType: z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,63}$/)
        .optional(),
    entityId: z.uuid().optional(),
    actorId: z.uuid().optional(),
    from: AuditFilterInstant.optional(),
    to: AuditFilterInstant.optional(),
})
    // Se comparan los instantes por su valor, no como texto: con desplazamientos distintos el orden del texto
    // no es el del tiempo (p. ej., 10:00-05:00 es posterior a 14:00Z).
    .refine((value) => value.from === undefined || value.to === undefined || new Date(value.from).getTime() <= new Date(value.to).getTime(), {
    message: 'from_after_to',
    path: ['to'],
});
//# sourceMappingURL=requests.js.map